import {DateService} from 'app/core/services/date.service';
import {Template} from 'app/core/templatiser/templatiser.component';
import {PROVISIONAL_AGREEMENT_METADATA} from 'app/diary/properties/provisional-agreement-metadata';
import {SearchService} from 'app/search/search.service';
import {STATE_METADATA} from 'app/workflow/state-metadata';
import {SearchColumn} from './search-column';
import {SearchEntry} from './search-result';

/*
 * Display name:
 * --what the table header will show
 *
 * Display types:
 * --Date
 * --Duration (Days?)
 * --People
 * --Person
 * --Default (String or boolean or int)
 *
 * Display function:
 * --callback to translate database response to human readable info.
 */

export interface SearchableColumnMetadata {
  displayName: string;
  displayFunction: (entry: SearchEntry) => Template;
  minWidth?: string;
}

export type DisplayFunctionFactory = (col: SearchColumn) => (entry: SearchEntry) => Template;

const dateDisplayFunctionFactory: DisplayFunctionFactory = (col: SearchColumn) => {
  return (entry: SearchEntry) => {
    return {
      template: entry[col] ? '${date}' : '',
      arguments: entry[col] ? [entry[col] as number] : [],
    };
  };
};

const stringListDisplayFunctionFactory: DisplayFunctionFactory = (col: SearchColumn) => {
  return (entry: SearchEntry) => {
    if (entry[col] && !(entry[col] instanceof Array) && entry[col][0] && typeof entry[col][0] !== 'string') {
      throw new Error(
        'invalid data format for stringListDisplayFunction. Expected string[] but got ' + typeof entry[col][0] + '[]'
      );
    }
    return {
      template:
        entry[col] !== null && entry[col] !== undefined && entry[col].toString()
          ? (entry[col] as string[]).join(', ')
          : '',
      arguments: [],
    };
  };
};

const durationDisplayFunctionFactory: DisplayFunctionFactory = (col: SearchColumn) => {
  return (entry: SearchEntry) => {
    let template: Template;
    const millisecondsInNullEntry =
      col === SearchColumn.TIME_REMAINING_IN_SES ? DateService.DAYS_ALLOWED_IN_SES * DateService.LENGTH_OF_DAY : 0;
    if (!entry[col] || (entry[col] as number) === millisecondsInNullEntry) {
      template = {
        template: '',
        arguments: [],
      };
    } else {
      template = {
        template: '${duration}',
        arguments: [entry[col] as number, col !== SearchColumn.TIME_REMAINING_IN_SES],
      };
    }
    return template;
  };
};

const defaultDisplayFunctionFactory: DisplayFunctionFactory = (col: SearchColumn) => {
  return (entry: SearchEntry) => {
    const template: Template = {
      template: entry[col] !== null && entry[col] !== undefined && entry[col].toString() ? String(entry[col]) : '',
      arguments: [],
    };
    return template;
  };
};

const booleanDisplayFunctionFactory: DisplayFunctionFactory = (col: SearchColumn) => {
  return (entry: SearchEntry) => {
    const template: Template = {
      template: entry[col] ? 'Yes' : 'No',
      arguments: [],
    };
    return template;
  };
};

export const SEARCHABLE_COLUMN_METADATA: Record<SearchColumn, SearchableColumnMetadata> = {
  DEPARTURE_ID: {
    displayName: 'Departure ID',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DEPARTURE_ID),
  },
  DATE_DETERMINED: {
    displayName: 'Date determined',
    displayFunction: dateDisplayFunctionFactory(SearchColumn.DATE_DETERMINED),
  },
  DATE_RECEIVED_AT_HE: {
    displayName: 'Date received at NH',
    displayFunction: dateDisplayFunctionFactory(SearchColumn.DATE_RECEIVED_AT_HE),
  },
  DATE_PREVIOUS_STAGE_COMPLETED: {
    displayName: 'Date previous stage completed',
    displayFunction: dateDisplayFunctionFactory(SearchColumn.DATE_PREVIOUS_STAGE_COMPLETED),
  },
  TIME_IN_WORKFLOW_STATE: {
    displayName: 'Time in workflow state',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_WORKFLOW_STATE),
  },
  TIME_ENTERED_SES: {
    displayName: 'Time entered SES',
    displayFunction: dateDisplayFunctionFactory(SearchColumn.TIME_ENTERED_SES),
  },
  TIME_LEFT_SES: {
    displayName: 'Time left SES',
    displayFunction: dateDisplayFunctionFactory(SearchColumn.TIME_LEFT_SES),
  },
  TIME_IN_SES: {
    displayName: 'Time in SES',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_SES),
  },
  TIME_REMAINING_IN_SES: {
    displayName: 'Time remaining in SES',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_REMAINING_IN_SES),
  },
  TIME_IN_PM_APPRAISAL: {
    displayName: 'Time in PM appraisal',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_PM_APPRAISAL),
  },
  TIME_IN_PM_DETERMINATION: {
    displayName: 'Time in PM determination',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_PM_DETERMINATION),
  },
  TIME_OPEN: {
    displayName: 'Time open',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_OPEN),
  },
  TIME_IN_NEED_IDENTIFIED: {
    displayName: 'Time in need identified',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_NEED_IDENTIFIED),
  },
  TIME_IN_SUBMISSION_IN_PREPARATION: {
    displayName: 'Time in submission in preparation',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_SUBMISSION_IN_PREPARATION),
  },
  TIME_IN_PM_APPRAISAL_REWORK: {
    displayName: 'Time in PM rework',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_PM_APPRAISAL_REWORK),
  },
  TIME_IN_DAS_ADMIN: {
    displayName: 'Time with DAS Admin',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_DAS_ADMIN),
  },
  TIME_IN_DAS_ADMIN_REWORK: {
    displayName: 'Time in DAS Admin rework',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_DAS_ADMIN_REWORK),
  },
  TIME_IN_SPECIALIST_SUBMISSION_POINT: {
    displayName: 'Time with SSP',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_SPECIALIST_SUBMISSION_POINT),
  },
  TIME_IN_SPECIALIST_SUBMISSION_POINT_REWORK: {
    displayName: 'Tme in SSP rework',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_SPECIALIST_SUBMISSION_POINT_REWORK),
  },
  TIME_IN_SPECIALIST_REVIEW: {
    displayName: 'Time in Specialist review',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_SPECIALIST_REVIEW),
  },
  TIME_IN_REWORK_REQUIRED: {
    displayName: 'Time in Specialist rework',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_REWORK_REQUIRED),
  },
  TIME_IN_APPROVED: {
    displayName: 'Time since approved',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_APPROVED),
  },
  TIME_IN_APPROVED_WITH_CONDITIONS: {
    displayName: 'Time since approved with conditions',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_APPROVED_WITH_CONDITIONS),
  },
  TIME_IN_REJECTED: {
    displayName: 'Time since rejected',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_REJECTED),
  },
  TIME_IN_WITHDRAWN: {
    displayName: 'Time since withdrawn',
    displayFunction: durationDisplayFunctionFactory(SearchColumn.TIME_IN_WITHDRAWN),
  },
  DATE_PROVISIONAL_AGREEMENT_REQUESTED: {
    displayName: 'Date Provisional Agreement Requested',
    displayFunction: dateDisplayFunctionFactory(SearchColumn.DATE_PROVISIONAL_AGREEMENT_REQUESTED),
  },
  DATE_PROVISIONAL_AGREEMENT_REVIEWED: {
    displayName: 'Date Provisional Agreement Reviewed',
    displayFunction: dateDisplayFunctionFactory(SearchColumn.DATE_PROVISIONAL_AGREEMENT_REVIEWED),
  },
  NUMBER_OF_LOCATIONS: {
    displayName: 'Number of locations',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_LOCATIONS),
  },
  STRUCTURE_KEYS: {
    displayName: 'Structure keys',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.STRUCTURE_KEYS),
  },
  ALL_STRUCTURE_KEYS: {
    displayName: 'Structure keys',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ALL_STRUCTURE_KEYS),
  },
  CONFIDENTIAL: {
    displayName: 'Confidential',
    displayFunction: booleanDisplayFunctionFactory(SearchColumn.CONFIDENTIAL),
  },
  PROVISIONAL_AGREEMENT: {
    displayName: 'Provisional Agreement',
    displayFunction: (entry: SearchEntry) => {
      if (PROVISIONAL_AGREEMENT_METADATA[entry[SearchColumn.PROVISIONAL_AGREEMENT]] !== undefined) {
        return {
          template: PROVISIONAL_AGREEMENT_METADATA[entry[SearchColumn.PROVISIONAL_AGREEMENT]].displayName,
          arguments: [],
        };
      } else {
        return entry[SearchColumn.PROVISIONAL_AGREEMENT] !== null
          ? {template: entry[SearchColumn.PROVISIONAL_AGREEMENT], arguments: []}
          : null;
      }
    },
  },
  INNOVATION: {
    displayName: 'Innovation',
    displayFunction: booleanDisplayFunctionFactory(SearchColumn.INNOVATION),
  },
  YEAR_OF_ISSUE: {
    displayName: 'Year of issue',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.YEAR_OF_ISSUE),
  },
  ADDED_VALUE: {
    displayName: 'Cost benefit',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ADDED_VALUE),
  },
  CLAUSE_PARAGRAPH: {
    displayName: 'Clause paragraph',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.CLAUSE_PARAGRAPH),
  },
  DEPARTURE_SUMMARY: {
    displayName: 'Summary',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DEPARTURE_SUMMARY),
    minWidth: '400px',
  },
  DESIGN_ORGANISATION: {
    displayName: 'Design organisation',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DESIGN_ORGANISATION),
  },
  DESIGN_ORGANISATION_ID: {
    displayName: 'Design organisation',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DESIGN_ORGANISATION_ID),
  },
  PIN: {
    displayName: 'PIN',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.PIN),
  },
  STANDARD: {
    displayName: 'Standard',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.STANDARD),
    minWidth: '200px',
  },
  TITLE: {
    displayName: 'Title',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.TITLE),
    minWidth: '200px',
  },
  ROAD: {
    displayName: 'Road',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ROAD),
  },
  SCHEME_NAME: {
    displayName: 'Scheme name',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.SCHEME_NAME),
    minWidth: '200px',
  },
  FORM_OF_CONTRACT: {
    displayName: 'Form of contract',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.FORM_OF_CONTRACT),
  },
  OPERATION_OR_MAJOR_PROJECT_DIVISION: {
    displayName: 'Operations/Major project divisions',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.OPERATION_OR_MAJOR_PROJECT_DIVISION),
  },
  TEMPLATE: {
    displayName: 'Template',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.TEMPLATE),
  },
  DEPARTURE_CRITICALITY: {
    displayName: 'Departure criticality',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DEPARTURE_CRITICALITY),
  },
  PROJECT_SAFETY_RISK: {
    displayName: 'Project safety risk',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.PROJECT_SAFETY_RISK),
  },
  DEPARTURE_SAFETY_RISK: {
    displayName: 'Departure safety risk',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DEPARTURE_SAFETY_RISK),
  },
  REVISION: {
    displayName: 'Revision',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.REVISION),
  },
  DEPARTURE_ID_NUMBER: {
    displayName: 'Departure ID - Revision',
    displayFunction: (entry: SearchEntry) => {
      return {template: entry[SearchColumn.DEPARTURE_ID_NUMBER] + ' - ' + entry[SearchColumn.REVISION], arguments: []};
    },
  },
  DEPARTURE_ID_NUMBER_AND_REVISION: {
    displayName: 'Departure ID - Revision',
    displayFunction: (entry: SearchEntry) => {
      const entryValue: number = entry.DEPARTURE_ID_NUMBER_AND_REVISION;
      let returnString: string;
      if (entry !== null && entry !== undefined && entry.toString()) {
        if (entryValue - Math.floor(entryValue) === 0) {
          returnString = entryValue + ' - 0';
        } else {
          const departureNumber = Math.floor(entryValue);
          const revisionNumber =
            entryValue * SearchService.CONVERT_DECIMAL_REVISION_TO_INT -
            Math.floor(entryValue) * SearchService.CONVERT_DECIMAL_REVISION_TO_INT;
          returnString = departureNumber + ' - ' + Math.round(revisionNumber);
        }
        return {template: returnString, arguments: []};
      } else {
        return {template: '', arguments: []};
      }
    },
  },
  CATEGORY: {
    displayName: 'Standard category',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.CATEGORY),
  },
  SUBJECT: {
    displayName: 'Standard subject',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.SUBJECT),
  },
  DESCRIPTION: {
    displayName: 'Standard description',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DESCRIPTION),
    minWidth: '300px',
  },
  VOLUME: {
    displayName: 'Standard volume',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.VOLUME),
  },
  ADDITIONAL_TECHNICAL_REVIEWERS_UUIDS: {
    displayName: 'Additional technical reviewers',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.ADDITIONAL_TECHNICAL_REVIEWERS_UUIDS),
  },
  DESIGNERS_UUIDS: {
    displayName: 'Designers',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.DESIGNERS_UUIDS),
  },
  CURRENT_AND_PREVIOUS_PROJECT_MANAGER_UUIDS: {
    displayName: 'Current and previous project managers',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.CURRENT_AND_PREVIOUS_PROJECT_MANAGER_UUIDS),
  },
  ASSIGNEE_UUID: {
    displayName: 'Assignee',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ASSIGNEE_UUID),
  },
  AUTHORISING_SIGNATORY_UUID: {
    displayName: 'Authorising signatory',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.AUTHORISING_SIGNATORY_UUID),
  },
  PROJECT_MANAGER_UUID: {
    displayName: 'Project manager',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.PROJECT_MANAGER_UUID),
  },
  PROPOSER_UUID: {
    displayName: 'Proposer',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.PROPOSER_UUID),
  },
  SSP_UUID: {
    displayName: 'Specialist submission point',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.SSP_UUID),
  },
  TECHNICAL_SPECIALIST_UUID: {
    displayName: 'Technical specialist',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.TECHNICAL_SPECIALIST_UUID),
  },
  ADDITIONAL_TECHNICAL_REVIEWERS_NAMES: {
    displayName: 'Additional technical reviewers',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ADDITIONAL_TECHNICAL_REVIEWERS_NAMES),
  },
  DESIGNERS_NAMES: {
    displayName: 'Designers',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DESIGNERS_NAMES),
  },
  CURRENT_AND_PREVIOUS_PROJECT_MANAGER_NAMES: {
    displayName: 'Current and previous project managers',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.CURRENT_AND_PREVIOUS_PROJECT_MANAGER_NAMES),
  },
  ASSIGNEE_NAME: {
    displayName: 'Assignee',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ASSIGNEE_NAME),
  },
  AUTHORISING_SIGNATORY_NAME: {
    displayName: 'Authorising signatory',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.AUTHORISING_SIGNATORY_NAME),
  },
  PROJECT_MANAGER_NAME: {
    displayName: 'Project manager',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.PROJECT_MANAGER_NAME),
  },
  PROPOSER_NAME: {
    displayName: 'Proposer',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.PROPOSER_NAME),
  },
  SSP_NAME: {
    displayName: 'Specialist submission point',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.SSP_NAME),
  },
  TECHNICAL_SPECIALIST_NAME: {
    displayName: 'Technical specialist',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.TECHNICAL_SPECIALIST_NAME),
  },
  WORK_FLOW_STATE: {
    displayName: 'Workflow state',
    displayFunction: (entry: SearchEntry) => {
      if (STATE_METADATA[entry[SearchColumn.WORK_FLOW_STATE]] !== undefined) {
        return {template: STATE_METADATA[entry[SearchColumn.WORK_FLOW_STATE]].displayName, arguments: []};
      } else {
        return entry[SearchColumn.WORK_FLOW_STATE] !== null
          ? {template: entry[SearchColumn.WORK_FLOW_STATE], arguments: []}
          : null;
      }
    },
  },
  PM_TO_VIEW_POST_RECOMMENDATION: {
    displayName: 'PM to view post recommendation',
    displayFunction: booleanDisplayFunctionFactory(SearchColumn.PM_TO_VIEW_POST_RECOMMENDATION),
  },
  ALL_REASON_CODES: {
    displayName: 'All reason codes',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.ALL_REASON_CODES),
  },
  LATEST_REASON_CODES: {
    displayName: 'Latest reason codes',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.LATEST_REASON_CODES),
  },
  DAS_2_BULK_DEPARTURE: {
    displayName: 'is DAS 2 Bulk Departure',
    displayFunction: booleanDisplayFunctionFactory(SearchColumn.DAS_2_BULK_DEPARTURE),
  },
  DAS_2_PARENT_DEPARTURE_ID: {
    displayName: 'DAS 2 Parent Departure ID',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DAS_2_PARENT_DEPARTURE_ID),
  },
  DAS_2_STATUS: {
    displayName: 'DAS 2 Status',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DAS_2_STATUS),
  },
  DAS_2_REFERENCE_TYPE: {
    displayName: 'DAS 2 Reference Type',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DAS_2_REFERENCE_TYPE),
  },
  DAS_2_DEALING_OFFICER: {
    displayName: 'DAS 2 Dealing Officer',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DAS_2_DEALING_OFFICER),
  },
  DAS_2_AUTHORISING_OFFICER: {
    displayName: 'DAS 2 Authorising Officer',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DAS_2_AUTHORISING_OFFICER),
  },
  DAS_2_TENDERER_DESIGNER: {
    displayName: 'DAS 2 Tenderer Designer',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DAS_2_TENDERER_DESIGNER),
  },
  DAS_2_DATE_ENTERED: {
    displayName: 'DAS 2 Date Entered',
    displayFunction: dateDisplayFunctionFactory(SearchColumn.DAS_2_DATE_ENTERED),
  },
  DAS_2_DECISION_SUMMARY: {
    displayName: 'DAS 2 Decision Summary',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.DAS_2_DECISION_SUMMARY),
  },
  REALISED_ADDED_VALUE: {
    displayName: 'Realised cost benefit',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.REALISED_ADDED_VALUE),
  },
  NUMBER_OF_LOCATIONS_INCORPORATED: {
    displayName: 'Number of locations incorporated',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_LOCATIONS_INCORPORATED),
  },
  NUMBER_OF_LOCATIONS_NOT_INCORPORATED: {
    displayName: 'Number of locations not incorporated',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_LOCATIONS_NOT_INCORPORATED),
  },
  NUMBER_OF_LOCATIONS_TO_BE_CONFIRMED: {
    displayName: 'Number of locations to be confirmed',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_LOCATIONS_TO_BE_CONFIRMED),
  },
  NUMBER_OF_LOCATIONS_INCORPORATED_AND_DATE_PROVIDED: {
    displayName: 'Number of locations incorporated and date provided',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_LOCATIONS_INCORPORATED_AND_DATE_PROVIDED),
  },
  NUMBER_OF_LOCATIONS_WITH_STATUS_WILL_NOT_BE_CONFIRMED: {
    displayName: 'Number of locations with status will not be confirmed',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_LOCATIONS_WITH_STATUS_WILL_NOT_BE_CONFIRMED),
  },
  NUMBER_OF_LOCATIONS_WITH_POST_DETERMINATION_ACTIONS_REMAINING: {
    displayName: 'Number of locations with post determination actions remaining',
    displayFunction: defaultDisplayFunctionFactory(
      SearchColumn.NUMBER_OF_LOCATIONS_WITH_POST_DETERMINATION_ACTIONS_REMAINING
    ),
  },
  ALL_LOCATION_POST_DETERMINATION_ACTIONS_COMPLETE: {
    displayName: 'All location post determination actions complete',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ALL_LOCATION_POST_DETERMINATION_ACTIONS_COMPLETE),
  },
  NUMBER_OF_CONDITIONS: {
    displayName: 'Number of conditions',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_CONDITIONS),
  },
  NUMBER_OF_CONDITIONS_TO_BE_CONFIRMED: {
    displayName: 'Number of conditions to be confirmed',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_CONDITIONS_TO_BE_CONFIRMED),
  },
  NUMBER_OF_CONDITIONS_ADHERED_TO: {
    displayName: 'Number of conditions adhered to',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_CONDITIONS_ADHERED_TO),
  },
  NUMBER_OF_CONDITIONS_NOT_ADHERED_TO: {
    displayName: 'Number of conditions not adhered to',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_CONDITIONS_NOT_ADHERED_TO),
  },
  NUMBER_OF_CONDITIONS_PARTIALLY_ADHERED_TO: {
    displayName: 'Number of conditions partially adhered to',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_CONDITIONS_PARTIALLY_ADHERED_TO),
  },
  NUMBER_OF_CONDITIONS_WITH_STATUS_WILL_NOT_BE_CONFIRMED: {
    displayName: 'Number of conditions with status will not be confirmed',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_CONDITIONS_WITH_STATUS_WILL_NOT_BE_CONFIRMED),
  },
  NUMBER_OF_CONDITIONS_WITHOUT_COMMENT_PROVIDED: {
    displayName: 'Number of conditions without comment provided',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_CONDITIONS_WITHOUT_COMMENT_PROVIDED),
  },
  NUMBER_OF_CONDITIONS_WITH_POST_DETERMINATION_ACTIONS_REMAINING: {
    displayName: 'Number of conditions with post determination actions remaining',
    displayFunction: defaultDisplayFunctionFactory(
      SearchColumn.NUMBER_OF_CONDITIONS_WITH_POST_DETERMINATION_ACTIONS_REMAINING
    ),
  },
  ALL_CONDITIONS_ADHERED_TO: {
    displayName: 'All conditions adhered to',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ALL_CONDITIONS_ADHERED_TO),
  },
  ALL_CONDITION_POST_DETERMINATION_ACTIONS_COMPLETE: {
    displayName: 'All conditions post determination actions complete',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ALL_CONDITION_POST_DETERMINATION_ACTIONS_COMPLETE),
  },
  ALL_POST_DETERMINATION_ACTIONS_COMPLETE: {
    displayName: 'All post determination actions complete',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.ALL_POST_DETERMINATION_ACTIONS_COMPLETE),
  },
  PM_LIAISED: {
    displayName: 'PM has confirmed liaising',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.PM_LIAISED),
  },
  NUMBER_OF_LINKED_DEPARTURES: {
    displayName: 'Number of linked departures',
    displayFunction: defaultDisplayFunctionFactory(SearchColumn.NUMBER_OF_LINKED_DEPARTURES),
  },
  LINKED_SIMILAR_DEPARTURE_IDS: {
    displayName: 'Linked departures: Similar DAS IDs',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.LINKED_SIMILAR_DEPARTURE_IDS),
  },
  LINKED_REPEAT_DEPARTURE_IDS: {
    displayName: 'Linked departures: Repeat DAS IDs',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.LINKED_REPEAT_DEPARTURE_IDS),
  },
  LINKED_ASSOCIATED_DEPARTURE_IDS: {
    displayName: 'Linked departures: Associated DAS IDs',
    displayFunction: stringListDisplayFunctionFactory(SearchColumn.LINKED_ASSOCIATED_DEPARTURE_IDS),
  },
};
